(() => {
  const ANIM_DURATION = 320;
  const ANIM_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";

  const prefersReducedMotion = () =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const initScrollAnimation = (section) => {
    if (!section.classList.contains("js-video-with-faq-animate")) return;

    const targets = section.querySelectorAll(".js-video-with-faq-target");
    if (!targets.length) return;

    const observer = new IntersectionObserver(
      (entries, currentObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-inview");
          currentObserver.unobserve(entry.target);
        });
      },
      {
        threshold: 0.2,
        rootMargin: "0px 0px -8% 0px",
      },
    );

    targets.forEach((target) => observer.observe(target));
  };

  // The Framer design reveals the FAQ heading letter by letter, so the plain
  // text node is rebuilt as per-word / per-letter spans. Words stay unbreakable
  // so the inline-block letters never wrap mid-word.
  const initSplitHeading = (section) => {
    const heading = section.querySelector("[data-vwf-split-letters]");
    if (!heading || heading.dataset.vwfSplit === "true") return;

    const chunks = heading.textContent.trim().split(/(\s+)/);
    heading.textContent = "";

    let index = 0;
    chunks.forEach((chunk) => {
      if (chunk === "") return;

      if (/^\s+$/.test(chunk)) {
        heading.appendChild(document.createTextNode(" "));
        return;
      }

      const word = document.createElement("span");
      word.className = "video-with-faq__word";

      Array.from(chunk).forEach((character) => {
        const letter = document.createElement("span");
        letter.className = "video-with-faq__letter";
        letter.textContent = character;
        letter.style.setProperty("--vwf-letter-index", index);
        index += 1;
        word.appendChild(letter);
      });

      heading.appendChild(word);
    });

    heading.dataset.vwfSplit = "true";
    heading.classList.add("is-split");
  };

  // <details> cannot transition its own height, so the answer wrapper is
  // animated instead and the open attribute is flipped around the animation.
  const initAccordionItem = (item, onOpen) => {
    const summary = item.querySelector(".video-with-faq__summary");
    const panel = item.querySelector(".video-with-faq__answer-wrap");
    if (!summary || !panel) return null;

    let animation = null;
    let isClosing = false;

    const settle = () => {
      animation = null;
      panel.style.height = "";
    };

    const open = () => {
      if (prefersReducedMotion()) {
        item.open = true;
        item.classList.add("is-open");
        return;
      }

      // Read the live height before cancelling: cancel() snaps the panel back
      // to its natural size, which would make a mid-close reversal jump.
      const start = isClosing ? `${panel.offsetHeight}px` : "0px";
      animation?.cancel();
      isClosing = false;
      item.open = true;
      item.classList.add("is-open");

      animation = panel.animate(
        { height: [start, `${panel.scrollHeight}px`] },
        { duration: ANIM_DURATION, easing: ANIM_EASING },
      );
      animation.onfinish = settle;
      animation.oncancel = settle;
    };

    const close = () => {
      if (prefersReducedMotion()) {
        item.open = false;
        item.classList.remove("is-open");
        return;
      }

      const start = `${panel.offsetHeight}px`;
      animation?.cancel();
      isClosing = true;
      item.classList.remove("is-open");

      animation = panel.animate(
        { height: [start, "0px"] },
        { duration: ANIM_DURATION, easing: ANIM_EASING },
      );
      animation.onfinish = () => {
        isClosing = false;
        item.open = false;
        settle();
      };
      animation.oncancel = () => {
        isClosing = false;
        settle();
      };
    };

    summary.addEventListener("click", (event) => {
      event.preventDefault();
      if (item.open && !isClosing) {
        close();
        return;
      }
      open();
      onOpen(item);
    });

    if (item.open) item.classList.add("is-open");

    return { close, isOpen: () => item.open && !isClosing };
  };

  const initAccordion = (section) => {
    const list = section.querySelector("[data-vwf-faq-list]");
    if (!list) return;

    const singleOpen = list.hasAttribute("data-vwf-single-open");
    const controllers = new Map();

    const closeOthers = (openedItem) => {
      if (!singleOpen) return;
      controllers.forEach((controller, item) => {
        if (item === openedItem) return;
        if (controller.isOpen()) controller.close();
      });
    };

    list.querySelectorAll(".video-with-faq__item").forEach((item) => {
      const controller = initAccordionItem(item, closeOthers);
      if (controller) controllers.set(item, controller);
    });
  };

  const initVideoPlayer = (section) => {
    const player = section.querySelector("[data-vwf-player]");
    if (!player) return;

    const iframe = player.querySelector(".video-with-faq__iframe");
    const video = player.querySelector(".video-with-faq__video");
    const playBtn = player.querySelector(".video-with-faq__play");
    const embedBase = player.getAttribute("data-embed-src");
    if (!video && !(iframe && embedBase)) return;

    // Native Shopify video: click toggles play/pause and the play overlay
    // reappears while paused. The YouTube iframe can only be started once.
    const toggle = () => {
      if (video) {
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
        return;
      }

      if (player.classList.contains("is-playing")) return;
      const separator = embedBase.includes("?") ? "&" : "?";
      iframe.src = `${embedBase}${separator}autoplay=1`;
      player.classList.add("is-playing");
      player.setAttribute("aria-label", "Playing video");
    };

    if (video) {
      video.addEventListener("play", () => {
        player.classList.add("is-playing");
        player.classList.remove("is-paused");
        player.setAttribute("aria-label", "Pause video");
      });
      video.addEventListener("pause", () => {
        player.classList.add("is-paused");
        player.setAttribute("aria-label", "Play video");
      });
    }

    player.addEventListener("click", (event) => {
      // Let the native video controls (and the browser's own click-to-pause)
      // handle clicks on the media itself; our toggle drives the overlay only.
      if (event.target === iframe || event.target === video) return;
      toggle();
    });

    playBtn?.addEventListener("click", (event) => {
      event.stopPropagation();
      toggle();
    });

    player.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      toggle();
    });
  };

  const initSection = (section) => {
    initSplitHeading(section);
    initScrollAnimation(section);
    initAccordion(section);
    initVideoPlayer(section);
  };

  document.querySelectorAll("[data-video-with-faq]").forEach(initSection);

  document.addEventListener("shopify:section:load", (event) => {
    const section = event.target.querySelector("[data-video-with-faq]");
    if (section) initSection(section);
  });
})();
